/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Filter16Filled, List16Filled } from '@fluentui/react-icons';
import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FileType } from 'src/core/types/FileType';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { QnaMakerDiscardModal } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerDiscardModal';
import { QnaMakerQnaCardLegendModal } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaCardLegendModal';
import { DeletionOperations } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';

export type QnaMakerQnaPairCommandBarProps = TableCommandBarProps<QnaMakerQnaStore> & {
    /**
     * A boolean flag indicating if there are unsaved changes.
     */
    isInDirtyState: boolean;

    /**
     * A boolean flag indicating if there are no qnas.
     */
    isInEmptyState: boolean;

    /**
     * A callback to toggle isFilterSelected flag.
     */
    onFilter: () => void;

    /**
     * A callback on save changes click.
     */
    onSave: () => void;

    /**
     * A callback on discard changes click.
     */
    onDiscard: () => void;

    /**
     * A callback on test button click.
     */
    onTest: () => void;

    /**
     * A callback to delete qnas.
     */
    onDelete: (operation: DeletionOperations) => void;

    /**
     * A callback on import button click.
     */
    onImport: (fileFormat: Extract<FileType, 'xlsx' | 'tsv'>) => void;

    /**
     * A callback on export button click.
     */
    onExport: (fileFormat: Extract<FileType, 'xlsx' | 'tsv'>) => void;

    /**
     * A boolean flag indicating if there are no qnas.
     */
    isAddQnaPairOn: boolean;

    /**
     * A callback on add qna button click.
     */
    onAddNewQna: () => void;

    /**
     * A boolean flag indicating if there is a save operation in the background
     * to disable all edit actions.
     */
    areEditActionsDisabled: boolean;

    /**
     * A boolean flag indicating if the command bar should be displayed in full view
     */
    isInExpandedState: boolean;

    /**
     * A boolean flag indicating if delete should be disabled
     */
    isDeleteDisabled: boolean;

    /**
     * A boolean flag indicating if filter blade is open
     */
    isFilterBladeOpen: boolean;

    /**
     * A boolean flag indicating if test pane is open
     */
    isTestPaneOpen: boolean;
};

const messages = defineMessages({
    searchPlaceholder: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.searchPlaceholder',
        defaultMessage: 'Search pairs'
    },
    addQuestionPair: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.addQuestionPair',
        defaultMessage: 'Add'
    },
    saveChanges: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.saveChanges',
        defaultMessage: 'Save'
    },
    discardChanges: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.discardChanges',
        defaultMessage: 'Discard'
    },
    test: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.test',
        defaultMessage: 'Test'
    },
    delete: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.delete',
        defaultMessage: 'Delete'
    },
    deletePairs: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.deletePairs',
        defaultMessage: 'Delete pair(s)'
    },
    deleteFollowUpPrompts: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.deleteFollowUpPrompts',
        defaultMessage: 'Delete follow up prompts'
    },
    deleteMetadata: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.deleteMetadata',
        defaultMessage: 'Delete metadata'
    },
    filter: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.filter',
        defaultMessage: 'Filter'
    },
    import: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.import',
        defaultMessage: 'Import questions and answers'
    },
    importSubMenuAriaLabel: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.importSubMenuAriaLabel',
        defaultMessage: 'Import questions and answers menu'
    },
    export: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.export',
        defaultMessage: 'Export questions and answers'
    },
    exportSubMenuAriaLabel: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.exportSubMenuAriaLabel',
        defaultMessage: 'Export questions and answers menu'
    },
    exportQnasExcel: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.exportQnasExcel',
        defaultMessage: 'Export as Excel'
    },
    exportQnasTsv: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.exportQnasTsv',
        defaultMessage: 'Export as TSV'
    },
    importQnasExcel: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.importQnasExcel',
        defaultMessage: 'Import as Excel'
    },
    importQnasTsv: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.importQnasTsv',
        defaultMessage: 'Import as TSV'
    },
    context: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.context',
        defaultMessage: 'Context'
    },
    metadata: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.metadata',
        defaultMessage: 'Metadata'
    },
    exportImportItems: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.exportImportItems',
        defaultMessage: 'Import/Export'
    },
    legendModal: {
        id: 'qnaMaker.qnaMakerQnaPairCommandBar.legendModal',
        defaultMessage: 'Legend modal'
    }
});

/**
 * Command bar rendering all available models and filter options
 */
export const QnaMakerQnaPairCommandBar = observer((props: QnaMakerQnaPairCommandBarProps) => {
    const {
        onSave,
        onTest,
        onFilter,
        onDelete,
        onExport,
        onImport,
        onDiscard,
        onAddNewQna,
        isInDirtyState,
        isAddQnaPairOn,
        isInEmptyState,
        isDeleteDisabled,
        isInExpandedState,
        isFilterBladeOpen,
        isTestPaneOpen,
        areEditActionsDisabled
    } = props;

    const { localize } = useLocalization();
    const { uiStore, rootStore } = useGlobalState();
    const addButtonIdRef = React.useRef<string>(getId());
    const deleteButtonIdRef = React.useRef<string>(getId());
    const saveChangesButtonIdRef = React.useRef<string>(getId());
    const discardChangesButtonIdRef = React.useRef<string>(getId());
    const testButtonIdRef = React.useRef<string>(getId());
    const filterButtonIdRef = React.useRef<string>(getId());
    const importExportButtonIdRef = React.useRef<string>(getId());

    const [isAddButtonDisabled, setIsAddButtonDisabled] = React.useState(true);
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = React.useState(true);
    const [isDiscardButtonDisabled, setIsDiscardButtonDisabled] = React.useState(true);
    const [isDeleteButtonDisabled, setIsDeleteButtonDisabled] = React.useState(true);
    const [lastFocusedElement, setLastFocusedElement] = React.useState(null);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const [isLegendModalHidden, setIsLegendModalHidden] = React.useState(true);
    const [isDiscardModalHidden, setIsDiscardModalHidden] = React.useState(true);

    const buttonProps = (text: string) =>
        isInExpandedState
            ? {
                  text
              }
            : {
                  title: text
              };

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('QnaTest');
            dequeueTeachingBubble('QnaDelete');
            dequeueTeachingBubble('QnaFilter');
            dequeueTeachingBubble('QnaSaveChanges');
            dequeueTeachingBubble('QnaAddQuestionPair');
        },
        []
    );

    React.useEffect(() => {
        if (!isAddButtonDisabled && !uiStore.teachingBubbles.QnaAddQuestionPair) {
            createTeachingBubble(
                'QnaAddQuestionPair',
                localize(qnaMakerTeachingBubbleMessages.addQuestionPairBody),
                localize(qnaMakerTeachingBubbleMessages.addQuestionPairTitle),
                `#${addButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (!isDeleteButtonDisabled && !uiStore.teachingBubbles.QnaDelete && !uiStore.teachingBubbles.SynonymDelete) {
            createTeachingBubble(
                'QnaDelete',
                localize(qnaMakerTeachingBubbleMessages.qnaDeleteBody),
                localize(qnaMakerTeachingBubbleMessages.qnaDeleteTitle),
                `#${deleteButtonIdRef.current}`,
                true
            );

            return;
        }

        if (
            isInDirtyState &&
            !isSaveButtonDisabled &&
            !uiStore.teachingBubbles.QnaSaveChanges &&
            !uiStore.teachingBubbles.SynonymSaveChanges
        ) {
            createTeachingBubble(
                'QnaSaveChanges',
                localize(qnaMakerTeachingBubbleMessages.qnaSaveChangesBody),
                localize(qnaMakerTeachingBubbleMessages.saveChangesTitle),
                `#${saveChangesButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            !isInEmptyState &&
            !isInDirtyState &&
            !uiStore.teachingBubbles.QnaTest &&
            uiStore.teachingBubbles.QnaSaveChanges &&
            !uiStore.skippedTeachingBubbles.QnaSaveChanges
        ) {
            createTeachingBubble(
                'QnaTest',
                localize(qnaMakerTeachingBubbleMessages.testBody),
                localize(qnaMakerTeachingBubbleMessages.testTitle),
                `#${testButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            !isInEmptyState &&
            uiStore.teachingBubbles.QnaTest &&
            !uiStore.teachingBubbles.QnaFilter &&
            !uiStore.skippedTeachingBubbles.QnaTest
        ) {
            createTeachingBubble(
                'QnaFilter',
                localize(qnaMakerTeachingBubbleMessages.filterBody),
                localize(qnaMakerTeachingBubbleMessages.filterTitle),
                `#${filterButtonIdRef.current}`,
                true,
                true
            );
        }

        if (
            isInDirtyState &&
            !isDiscardButtonDisabled &&
            !uiStore.teachingBubbles.QnaSaveChanges &&
            !uiStore.teachingBubbles.QnaDiscardChanges
        ) {
            createTeachingBubble(
                'QnaDiscardChanges',
                localize(qnaMakerTeachingBubbleMessages.qnaDiscardChangesBody),
                localize(qnaMakerTeachingBubbleMessages.discardChangesTitle),
                `#${discardChangesButtonIdRef.current}`,
                true
            );
        }
    }, [
        isInDirtyState,
        isInEmptyState,
        isAddButtonDisabled,
        isSaveButtonDisabled,
        isDeleteButtonDisabled,
        Object.values(uiStore.teachingBubbles)
    ]);

    React.useEffect(() => {
        if (!isFilterBladeOpen && lastFocusedElement === 'filter') {
            const filterButton = document.querySelector('.filterButton') as HTMLElement | null;
            filterButton?.focus();
        }
        if (!isTestPaneOpen && lastFocusedElement === 'test') {
            const testPane = document.getElementById(testButtonIdRef.current) as HTMLElement | null;
            testPane?.focus();
        }
    }, [isFilterBladeOpen, isTestPaneOpen]);

    const deleteMenuItems: IContextualMenuItem[] = [
        {
            key: 'pairs',
            text: localize(messages.deletePairs),
            onClick: () => onDelete('qna')
        },
        {
            key: 'followUpPrompts',
            text: localize(messages.deleteFollowUpPrompts),
            onClick: () => onDelete('followUpPrompts')
        },
        {
            key: 'metadata',
            text: localize(messages.deleteMetadata),
            onClick: () => onDelete('metadata')
        }
    ];

    const getImportMenuItem = () =>
        !canWrite
            ? {
                  key: 'import',
                  onRender: () => (
                      <RBACTooltip
                          hidden={false}
                          displayedUserRole={displayedUserRole}
                          data-automation-id="importQnaButtonLanguageToolTip"
                          componentSurroundedByToolTip={
                              <CommandBarButton
                                  disabled
                                  role="menuitem"
                                  name={localize(messages.import)}
                                  text={localize(messages.import)}
                                  data-automation-id="importQnaButton"
                                  styles={{ root: { height: '40px' } }}
                                  ariaLabel={localize(messages.import)}
                                  menuIconProps={{ iconName: 'Upload' }}
                              />
                          }
                      />
                  )
              }
            : {
                  key: 'import',
                  role: 'menuitem',
                  text: localize(messages.import),
                  iconProps: { iconName: 'Upload' },
                  ariaLabel: localize(messages.import),
                  subMenuProps: {
                      id: 'importSubMenu',
                      ariaLabel: localize(messages.importSubMenuAriaLabel),
                      items: [
                          {
                              key: 'importExcel',
                              role: 'menuitem',
                              name: localize(messages.importQnasExcel),
                              onClick: () => onImport('xlsx')
                          },
                          { key: 'importTsv', role: 'menuitem', name: localize(messages.importQnasTsv), onClick: () => onImport('tsv') }
                      ]
                  }
              };

    const importExportMenuItems: IContextualMenuItem[] = [
        getImportMenuItem(),
        {
            key: 'export',
            role: 'menuitem',
            disabled: isInEmptyState,
            text: localize(messages.export),
            iconProps: { iconName: 'Download' },
            ariaLabel: localize(messages.export),
            subMenuProps: {
                id: 'exportSubMenu',
                ariaLabel: localize(messages.exportSubMenuAriaLabel),
                items: [
                    { key: 'exportExcel', role: 'menuitem', name: localize(messages.exportQnasExcel), onClick: () => onExport('xlsx') },
                    { key: 'exportTsv', role: 'menuitem', name: localize(messages.exportQnasTsv), onClick: () => onExport('tsv') }
                ]
            }
        }
    ];

    const items: ICommandBarItemProps[] = [
        {
            key: 'add',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || isAddQnaPairOn || areEditActionsDisabled;
                setIsAddButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="addQuestionPairButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={onAddNewQna}
                                id={addButtonIdRef.current}
                                iconProps={{ iconName: 'Add' }}
                                disabled={!canWrite || _disabled}
                                data-automation-id="addQuestionPairButton"
                                ariaLabel={localize(messages.addQuestionPair)}
                                {...buttonProps(localize(messages.addQuestionPair))}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'test',
            onRender: (item: ICommandBarItemProps) => (
                <CommandBarButton
                    role="menuitem"
                    onClick={() => {
                        setLastFocusedElement('test');
                        onTest();
                    }}
                    disabled={isInEmptyState}
                    id={testButtonIdRef.current}
                    data-automation-id="testButton"
                    ariaLabel={localize(messages.test)}
                    iconProps={{ iconName: 'TestBeaker' }}
                    {...buttonProps(localize(messages.test))}
                    styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || isDeleteDisabled || areEditActionsDisabled || isInEmptyState;
                setIsDeleteButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="deleteQuestionPairButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                id={deleteButtonIdRef.current}
                                ariaLabel={localize(messages.delete)}
                                disabled={!canWrite || _disabled}
                                iconProps={{ iconName: 'Delete' }}
                                menuProps={{ items: deleteMenuItems }}
                                {...buttonProps(localize(messages.delete))}
                                data-automation-id="deleteQuestionPairButton"
                                styles={mergeStyleSets(getCommandBarButtonStyles(item.renderedInOverflow), {
                                    rootDisabled: { backgroundColor: 'white' }
                                })}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'saveChanges',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled =
                    item.disabled || !isInDirtyState || isAddQnaPairOn || areEditActionsDisabled || (isInEmptyState && !isInDirtyState);
                setIsSaveButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="saveChangesButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={onSave}
                                iconProps={{ iconName: 'Save' }}
                                disabled={!canWrite || _disabled}
                                id={saveChangesButtonIdRef.current}
                                ariaLabel={localize(messages.saveChanges)}
                                data-automation-id="saveChangesButton"
                                {...buttonProps(localize(messages.saveChanges))}
                                styles={mergeStyleSets(getCommandBarButtonStyles(item.renderedInOverflow), {
                                    rootDisabled: { backgroundColor: 'white' }
                                })}
                            />
                        }
                    />
                );
            }
        },
        {
            key: 'importAndExport',
            onRender: (item: ICommandBarItemProps) => (
                <CommandBarButton
                    role="menuitem"
                    iconProps={{ iconName: 'Snow' }}
                    disabled={areEditActionsDisabled}
                    id={importExportButtonIdRef.current}
                    ariaLabel={localize(messages.exportImportItems)}
                    {...buttonProps(localize(messages.exportImportItems))}
                    menuProps={{
                        id: 'importExportMenuItems',
                        items: importExportMenuItems,
                        ariaLabel: localize(messages.exportImportItems)
                    }}
                    styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                />
            )
        },
        {
            key: 'discardChanges',
            onRender: (item: ICommandBarItemProps) => {
                const _disabled = item.disabled || !isInDirtyState || isAddQnaPairOn || areEditActionsDisabled || isInEmptyState;
                setIsDiscardButtonDisabled(!canWrite || _disabled);

                return (
                    <RBACTooltip
                        hidden={canWrite || _disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="discardChangesButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                role="menuitem"
                                onClick={() => setIsDiscardModalHidden(false)}
                                iconProps={{ iconName: 'ChromeClose' }}
                                disabled={!canWrite || _disabled}
                                id={discardChangesButtonIdRef.current}
                                data-automation-id="discardChangesButton"
                                ariaLabel={localize(messages.discardChanges)}
                                {...buttonProps(localize(messages.discardChanges))}
                                styles={mergeStyleSets(getCommandBarButtonStyles(item.renderedInOverflow), {
                                    rootDisabled: { backgroundColor: 'white' }
                                })}
                            />
                        }
                    />
                );
            }
        }
    ];

    const farItems: ICommandBarItemProps[] = [
        {
            key: 'filter',
            className: 'filterButton',
            role: 'menuitem',
            title: localize(messages.filter),
            onRenderIcon: () => <Filter16Filled primaryFill="#0078D4" />,
            onClick: () => {
                setLastFocusedElement('filter');
                onFilter();
            }
        },
        {
            key: 'legend',
            role: 'menuitem',
            title: localize(messages.legendModal),
            onRenderIcon: () => <List16Filled primaryFill="#0078D4" />,
            onClick: () => setIsLegendModalHidden(false)
        }
    ];

    const handleDiscard = () => {
        onDiscard();
        setIsDiscardModalHidden(true);
    };

    return (
        <>
            <LuisCommandBar items={items} farItems={farItems} showTopBorder={false} onReduceData={() => undefined} />
            <QnaMakerQnaCardLegendModal hidden={isLegendModalHidden} onDismiss={() => setIsLegendModalHidden(true)} />
            <QnaMakerDiscardModal onSubmit={handleDiscard} hidden={isDiscardModalHidden} onDismiss={() => setIsDiscardModalHidden(true)} />
        </>
    );
});
